// 観覧チケットの画面で使う純粋な関数（planning/2026-10-10-観覧チケット機能.md）。大会エントリーの画面では使わない

// 残りの表示。0なら売り切れ、席数の1割以下（最低1）なら残りわずか、それ以外は残りの枚数を出す
import { L, lang } from "./i18n";
import { normalizePhone } from "./entryForm";
export type Stock = { kind: "soldout" | "few" | "many"; text: string };
export function stockLabel(remaining: number, capacity: number): Stock {
  if (remaining <= 0) return { kind: "soldout", text: L("売り切れ", "Sold out") };
  if (remaining <= Math.max(1, Math.ceil(capacity * 0.1))) return { kind: "few", text: L("残りわずか", "Few left") };
  return { kind: "many", text: L(`残り${remaining}枚`, `${remaining} left`) };
}

// 枚数の選択肢。1〜上限で、残りより多い枚数は出さない
export function quantityOptions(maxPerOrder: number | undefined, remaining: number): number[] {
  const max = Math.min(maxPerOrder ?? 3, Math.max(0, remaining));
  return Array.from({ length: max }, (_, i) => i + 1);
}

export interface TicketRaw { eventId: string; classId: string; quantity: number; name: string; kana: string; email: string; phone: string; address: string; agreed: boolean; extra: Record<string, string>; }
// チケットの購入で送る内容。性別・生年月日・所属・保護者・誓約書は送らない
export function toTicketPayload(r: TicketRaw) {
  return {
    eventId: r.eventId, classId: r.classId, quantity: r.quantity,
    applicant: { name: r.name.trim(), kana: r.kana.trim(), email: r.email.trim().toLowerCase(), phone: normalizePhone(r.phone), address: r.address.trim() },
    guardian: null, extra: r.extra, agreed: r.agreed,
    // 英語の画面のときだけ lang を送る（メールの言語に使う）
    ...(lang() === "en" ? { lang: "en" as const } : {}),
  };
}

// 主催者ページで読んだ値を、引き換え用の QR として扱えるかを見る（サーバーでも確かめる）
export const REDEEM_PREFIX = "TRUNKIP-T1:";
export const looksLikeRedeemCode = (s: string) => /^TRUNKIP-T1:[0-9a-f]{64}$/i.test(s.trim());

// 購入画面でキャンセル待ちに登録するとき（売り切れの席種を選んだとき）に送る内容。
// キャンセル待ちに要るのは氏名・メール・希望枚数と、プライバシーポリシーへの同意だけである。電話・住所・ふりがなは送らない（planning/2026-10-10-観覧チケット機能.md 15節）
export function toWaitlistPayload(r: Pick<TicketRaw, "eventId" | "classId" | "quantity" | "name" | "email" | "agreed">) {
  return { eventId: r.eventId, classId: r.classId, name: r.name.trim(), email: r.email.trim().toLowerCase(), quantity: r.quantity, agreed: r.agreed, ...(lang() === "en" ? { lang: "en" as const } : {}) };
}
// 送る前に画面で確かめる。足りないものがあれば、その文言を返す（サーバーでも同じことを確かめる）
export function waitlistMissing(r: Pick<TicketRaw, "name" | "email" | "agreed" | "quantity">): string {
  if (!r.name.trim()) return L("氏名を入力してください。", "Please enter your name.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim())) return L("メールアドレスを正しく入力してください。", "Please enter a valid email address.");
  if (!(r.quantity >= 1)) return L("希望枚数をお選びください。", "Please choose the number of tickets.");
  if (!r.agreed) return L("利用規約とプライバシーポリシーへの同意にチェックしてください。", "Please check the box to agree to the Terms of Use and Privacy Policy.");
  return "";
}
// 席種の選択肢の名前。売り切れでキャンセル待ちを受け付けるときは、選べる形で「売り切れ・キャンセル待ち」と書く
export function classOptionLabel(c: { name: string; fee: string; soldOut: boolean }, waitlist: boolean): { label: string; disabled: boolean } {
  if (!c.soldOut) return { label: L(`${c.name}（${c.fee}）`, `${c.name} (${c.fee})`), disabled: false };
  return waitlist ? { label: L(`${c.name}（売り切れ・キャンセル待ち）`, `${c.name} (sold out – join the waitlist)`), disabled: false } : { label: L(`${c.name}（${c.fee}・売り切れ）`, `${c.name} (${c.fee}, sold out)`), disabled: true };
}

// 観覧チケットのイベントページの案内とボタン。販売前・販売終了・売り切れ・一時停止を分けて出す（販売前を「売り切れ」と誤解させない）
export function ticketCta(p: { phase: "before" | "open" | "after"; paused: boolean; totalRemaining: number; waitlist: boolean; openText: string }): { state: string; button: string; href: "buy" | "waitlist" | null } {
  if (p.paused) return { state: L("現在、販売を一時停止しています。再開まで今しばらくお待ちください。", "Sales are temporarily paused. Please wait until they reopen."), button: L("販売一時停止中", "Sales paused"), href: null };
  if (p.phase === "before") return { state: L(`販売開始は ${p.openText} です。開始の時刻になると、このページが自動で更新されます。`, `Sales open on ${p.openText}. This page will refresh automatically when sales open.`), button: L("販売開始前", "Not on sale yet"), href: null };
  if (p.phase === "after") return { state: L("販売は終了しました。たくさんのご購入をありがとうございました。", "Sales have ended. Thank you for your interest."), button: L("販売終了", "Sales ended"), href: null };
  if (p.totalRemaining > 0) return { state: "", button: L("チケットを購入する", "Buy tickets"), href: "buy" };
  const soldOut = L("申し訳ございません、すべての席種が売り切れました。", "We are sorry, all seat types are sold out.");
  return p.waitlist ? { state: soldOut, button: L("キャンセル待ちに登録する", "Join the waitlist"), href: "waitlist" } : { state: soldOut, button: L("売り切れ", "Sold out"), href: null };
}
